// ItemMessagesPanel — the item form's Messages tab (2026-07-18).
// Shows the purchasing@ emails linked to this item via the
// purchasing_email_items junction. Read-mostly: rows expand inline to
// the full body + attachments; owner/admin can unlink. NEW links are
// made from the Messages page ("+ Link an item" on an open email) —
// auto-link suggestions are a planned follow-up (FOLLOWUPS.md
// "Messages — remaining follow-ups").
//
// Data loads on demand when the tab opens (ItemHistory pattern), not
// in AppContext boot. Design language: hairlines + colored text on
// white, no fills. Guard tests: itemMessagesPanel.test.jsx.

import React, { useState, useEffect } from 'react';
import { useAppContext } from './AppContext';
import { supabase } from '../lib/supabase';
import { hasAdminAccess } from '../utils';
import {
  rowToPurchasingEmail, rowToEmailAttachment, emailCategoryMeta,
  emailSenderLabel, emailDisplayBody,
} from '../lib/purchasingEmails';

const COLOR_TEXT      = '#111111';
const COLOR_MUTED     = '#888880';
const COLOR_BORDER    = '#E0E0DC';
const COLOR_BORDER_DK = '#D8D7D2';
const COLOR_ALERT     = '#D4007A';

const EYEBROW = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: COLOR_MUTED };

export default function ItemMessagesPanel(p) {
  const ctx = useAppContext();
  const canManage = ctx.isOwner || hasAdminAccess(ctx.userEmail, ctx.firmSettings);
  const itemId = p.itemId;

  const [links, setLinks]       = useState(null);   // null = loading
  const [emails, setEmails]     = useState(null);   // null = not yet fetched
  const [loadError, setLoadError] = useState('');
  const [openId, setOpenId]     = useState(null);
  const [attachments, setAttachments] = useState([]);

  useEffect(function() {
    if (!itemId) { setLinks([]); setEmails([]); return; }
    let alive = true;
    supabase.from('purchasing_email_items').select('*').eq('item_id', itemId)
      .then(function(res) {
        if (!alive) return;
        if (res.error) { setLoadError('Could not load messages: ' + res.error.message); setLinks([]); setEmails([]); return; }
        const rows = res.data || [];
        setLinks(rows);
        if (!rows.length) { setEmails([]); return; }
        supabase.from('purchasing_emails')
          .select('id, message_id, from_email, from_name, to_email, subject, body_text, body_html, received_at, category, ai_summary, status, created_at')
          .in('id', rows.map(function(l) { return l.email_id; }))
          .order('received_at', { ascending: false })
          .then(function(r2) {
            if (!alive) return;
            if (r2.error) { setLoadError('Could not load messages: ' + r2.error.message); setEmails([]); return; }
            setEmails((r2.data || []).map(rowToPurchasingEmail));
          });
      });
    return function() { alive = false; };
  }, [itemId]);

  useEffect(function() {
    if (!openId) { setAttachments([]); return; }
    let alive = true;
    supabase.from('purchasing_email_attachments').select('*').eq('email_id', openId)
      .then(function(res) {
        if (alive) setAttachments(res.error ? [] : (res.data || []).map(rowToEmailAttachment));
      });
    return function() { alive = false; };
  }, [openId]);

  async function unlink(emailId) {
    const row = (links || []).find(function(l) { return l.email_id === emailId; });
    if (!row) return;
    const res = await supabase.from('purchasing_email_items').delete().eq('id', row.id);
    if (res.error) { alert('Unlink failed: ' + res.error.message); return; }
    setLinks(function(prev) { return (prev || []).filter(function(l) { return l.id !== row.id; }); });
    setEmails(function(prev) { return (prev || []).filter(function(e) { return e.id !== emailId; }); });
    if (openId === emailId) setOpenId(null);
  }

  async function openAttachment(att) {
    const res = await supabase.storage.from('purchasing-emails')
      .createSignedUrl(att.storagePath, 300);
    if (res.error || !res.data) { alert('Could not open attachment: ' + (res.error && res.error.message)); return; }
    window.open(res.data.signedUrl, '_blank', 'noopener');
  }

  const hint = (
    <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 14, lineHeight: 1.5 }}>
      Link emails from the Messages page — open an email and use “+ Link an item”.
    </div>
  );

  if (!itemId) {
    return <div style={{ fontSize: 12, color: COLOR_MUTED, padding: '12px 0' }}>
      Messages link to saved items — save this item first.
    </div>;
  }
  if (loadError) {
    return <div style={{ fontSize: 12, color: COLOR_ALERT, padding: '12px 0' }}>{loadError}</div>;
  }
  if (links === null || (links.length > 0 && emails === null)) {
    return <div style={{ fontSize: 12, color: COLOR_MUTED, padding: '12px 0' }}>Loading messages…</div>;
  }
  if (!(emails || []).length) {
    return <div style={{ padding: '12px 0' }}>
      <div style={{ fontSize: 12, color: COLOR_MUTED, fontStyle: 'italic' }}>
        No messages linked to this item yet.
      </div>
      {hint}
    </div>;
  }

  return (
    <div style={{ maxWidth: 760, padding: '4px 0' }}>
      <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>
        Messages — purchasing@ emails linked to this item
      </div>
      <div style={{ borderTop: '0.5px solid ' + COLOR_BORDER }}>
        {emails.map(function(e) {
          const cat = emailCategoryMeta(e.category);
          const isOpen = openId === e.id;
          return (
            <div key={e.id} style={{ borderBottom: '0.5px solid ' + COLOR_BORDER }}>
              <div
                onClick={function() { setOpenId(isOpen ? null : e.id); }}
                data-testid={'item-msg-row-' + e.id}
                style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '10px 4px', cursor: 'pointer' }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: COLOR_TEXT, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {emailSenderLabel(e)}
                </span>
                <span style={{ fontSize: 11, color: COLOR_TEXT, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.subject || '(no subject)'}
                  {e.aiSummary ? <span style={{ color: COLOR_MUTED, marginLeft: 8 }}>— {e.aiSummary}</span> : null}
                </span>
                <span style={{ fontSize: 10, color: cat.color, whiteSpace: 'nowrap', marginLeft: 'auto', flexShrink: 0 }}>
                  {e.category ? cat.label : ''}
                </span>
                <span style={{ fontSize: 10, color: COLOR_MUTED, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {String(e.receivedAt).slice(0, 10)}
                </span>
              </div>
              {isOpen ? (
                <div style={{ padding: '0 4px 14px 4px' }}>
                  <div style={{ fontSize: 10, color: COLOR_MUTED, marginBottom: 8 }}>
                    {emailSenderLabel(e)} &lt;{e.fromEmail}&gt; · {String(e.receivedAt).slice(0, 16).replace('T', ' ')}
                    {canManage ? (
                      <span
                        onClick={function(ev) { ev.stopPropagation(); unlink(e.id); }}
                        data-testid={'item-msg-unlink-' + e.id}
                        title="Remove this email's link to the item (the email stays on the Messages page)"
                        style={{ color: COLOR_MUTED, cursor: 'pointer', marginLeft: 10, borderBottom: '0.5px solid ' + COLOR_BORDER_DK }}>
                        Unlink
                      </span>
                    ) : null}
                  </div>
                  {attachments.length ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      {attachments.map(function(a) {
                        return (
                          <button key={a.id} onClick={function() { openAttachment(a); }}
                            style={{ height: 24, padding: '0 10px', borderRadius: 4,
                              border: '0.5px solid ' + COLOR_BORDER_DK, background: '#FFFFFF',
                              color: COLOR_TEXT, fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}>
                            ↓ {a.filename}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 12, color: COLOR_TEXT, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {emailDisplayBody(e) || <span style={{ color: COLOR_MUTED, fontStyle: 'italic' }}>(empty body)</span>}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {hint}
    </div>
  );
}
